import {Order} from '@mintdata-wsp/common/controllers/network/generated/requests/Order';
import {QueryCondition as RequestQueryCondition} from '@mintdata-wsp/common/controllers/network/generated/requests/QueryCondition';
import {QuerySettings} from '@mintdata-wsp/common/controllers/network/generated/requests/QuerySettings';
import {SelectColumn} from '@mintdata-wsp/common/controllers/network/generated/requests/SelectColumn';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {FrameData, IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {serialize} from '@mintdata/mint-kit/impl/util/serializr/src/core/serialize';

// hand-craft real request objects, because it's hard to support inheritance in code-generator
export class QueryCondition extends RequestQueryCondition
{
    constructor(public readonly type:string)
    {
        super();
    }
}

export class AndCondition extends QueryCondition
{
    constructor(
        public readonly args:Array<QueryCondition>
    )
    {
        super('and');
    }
}

export class OrCondition extends QueryCondition
{
    constructor(
        public readonly args:Array<QueryCondition>
    )
    {
        super('or');
    }
}

export class BinaryCondition extends QueryCondition
{
    constructor(
        public readonly op:string,
        public readonly left:any,
        public readonly right:any
    )
    {
        super('binary');
    }
}

export class InCondition extends QueryCondition
{
    constructor(
        public readonly column:string,
        public readonly values:Array<any>
    )
    {
        super('in');
    }
}

export class LikeCondition extends QueryCondition
{
    constructor(
        public readonly column:string,
        public readonly pattern:string,
        public readonly negate:boolean,
        public readonly ignoreCase:boolean
    )
    {
        super('like');
    }
}

export class BetweenCondition extends QueryCondition
{
    constructor(
        public readonly column:string,
        public readonly min:any,
        public readonly max:any
    )
    {
        super('between');
    }
}

const ORDER_DIRECTIONS = {
    'asc': true,
    'ascending': true,
    'desc': false,
    'descending': false
};

const AGGREGATES = ['min', 'max', 'avg', 'sum', 'count'];

export class ParseError extends Error
{
}

export class ParseResult
{
    constructor(readonly settings:QuerySettings,
                readonly query:QueryCondition)
    {
    }
}

class ConditionToken
{
    constructor(
        public readonly type:string,
        public readonly condition?:QueryCondition
    )
    {
    }
}

const EOF = new ConditionToken('eof');

class ConditionEvaluator
{
    private tokens:Array<ConditionToken>;
    private tokenInd;

    constructor(tokens:Array<ConditionToken>)
    {
        this.tokens = tokens;
        this.tokenInd = 0;
    }

    private _peekToken():ConditionToken
    {
        return this.tokens[this.tokenInd] || EOF;
    }

    private _consumeToken():ConditionToken
    {
        return this.tokens[this.tokenInd++] || EOF;
    }

    public evalCondition():QueryCondition
    {
        let result:QueryCondition = new AndCondition([]);

        if( this._peekToken().type !== 'eof' )
        {
            result = this._evalOr();
            if( this._peekToken().type !== 'eof' )
                throw new ParseError('#QUERY_PARSE_ERROR: invalid query condition');
        }

        return result;
    }

    private _evalOr():QueryCondition
    {
        const args:Array<QueryCondition> = [];

        while( true )
        {
            args.push(this._evalAnd());

            const nextToken = this._peekToken();
            if( nextToken.type === 'or' )
            {
                // consume `or`
                this._consumeToken();
                continue;
            }

            break;
        }

        return args.length == 1 ? args[0] : new OrCondition(args);
    }

    private _evalAnd():QueryCondition
    {
        const args:Array<QueryCondition> = [];

        while( true )
        {
            args.push(this._evalAtomic());

            const nextToken = this._peekToken();
            if( nextToken.type === 'and' )
            {
                // consume `and`
                this._consumeToken();
                continue;
            }

            // we can skip `and` and presume it by default
            if( nextToken.type === '(' || nextToken.type === 'condition' )
                continue;

            break;
        }
        return args.length == 1 ? args[0] : new AndCondition(args);
    }

    private _evalAtomic():QueryCondition
    {
        const nextToken = this._peekToken();
        if( nextToken.type === '(' )
            return this._evalParens();

        if( nextToken.type === 'condition' )
            return this._consumeToken().condition;

        throw new ParseError('#QUERY_PARSE_ERROR: invalid query condition');
    }

    private _evalParens():QueryCondition
    {
        let token = this._consumeToken();
        if( token.type !== '(' )
            throw new ParseError('#QUERY_PARSE_ERROR');

        const condition = this._evalOr();

        token = this._consumeToken();
        if( token.type !== ')' )
            throw new ParseError('#QUERY_PARSE_ERROR: invalid query condition "(" missing');

        return condition;
    }
}

// queryFrame format: https://docs.google.com/document/d/1b0R0uzB8-dF-418PLZ_hc7EGvS6wQbaXA-b_uvp2_-Y
export class QueryParser
{
    private readonly frameData:FrameData;

    private offset:number = null;
    private limit:number = null;
    private orderBy:Array<Order> = [];
    private groupBy:Array<string> = [];
    private columns:Array<SelectColumn> = [];

    private conditionTokens:Array<ConditionToken> = [];

    constructor(queryFrame:IFrame)
    {
        this.frameData = queryFrame.getDataUnsafe();
    }

    public parse():ParseResult
    {
        const exprStack = [];

        for( const rowData of this.frameData )
        {
            if( this._parseOptionRow(rowData) )
                continue;

            this._parseExprRow(rowData);
        }

        const settings = this._collectSettings();
        const condition = new ConditionEvaluator(this.conditionTokens).evalCondition();

        return new ParseResult(settings, condition);
    }

    private _collectSettings():QuerySettings
    {
        const offset = this.offset != null && this.offset > 0 ? this.offset : null;
        const limit = this.limit != null && this.limit > 0 ? this.limit : null;
        const orderBy = this.orderBy.length > 0 ? this.orderBy : null;
        const groupBy = this.groupBy.length > 0 ? this.groupBy : null;
        const columns = this.columns.length > 0 ? this.columns : null;

        return new QuerySettings(offset, limit, orderBy, columns, groupBy);
    }

    private _parseOptionRow(rowData:Array<IValue>):boolean
    {
        const settingName = this._parseAsString('setting', rowData[0]);

        // skip empty row
        if( settingName.length === 0 )
            return true;

        switch( settingName.toLowerCase() )
        {
            case 'offset':
            {
                this.offset = this._parseAsInt('offset', rowData[1]);
                return true;
            }
            case 'count':
            {
                this.limit = this._parseAsInt('limit', rowData[1]);
                return true;
            }
            case 'order by':
            case 'order_by':
            {
                const stringValues = this._parseStringValues('order_by', rowData.slice(1));
                if( stringValues.length == 0 || stringValues.length % 2 != 0 )
                    throw new ParseError('#QUERY_PARSER_ERROR: invalid value type in order_by');

                for( let i = 0; i < stringValues.length; i += 2 )
                {
                    const column = stringValues[i];
                    const direction = ORDER_DIRECTIONS[stringValues[i + 1]];
                    if( direction == null )
                        throw new ParseError('#QUERY_PARSER_ERROR: invalid value for sort direction');

                    this.orderBy.push(
                        new Order(column, direction)
                    );
                }

                return true;
            }
            case 'group by':
            case 'group_by':
            {
                const stringValues = this._parseStringValues('group_by', rowData.slice(1));
                if( stringValues.length == 0 )
                    throw new ParseError('#QUERY_PARSER_ERROR: invalid value type in group_by');

                this.groupBy.push(
                    ...stringValues
                );

                return true;
            }
            case 'columns':
            {
                this.columns.push(
                    ...this._parseColumns(rowData.slice(1))
                );
                return true;
            }
        }
        return false;
    }

    private _parseColumns(values:Array<IValue>):Array<SelectColumn>
    {
        const stringValues = this._parseStringValues('columns', values);
        if( stringValues.length == 0 )
            throw new ParseError('#QUERY_PARSER_ERROR: invalid value type in columns');

        return stringValues.map(stringColumn =>
        {
            const re = /^\s*(?:(\w+)|(\w+)\s*\(\s*(?:(distinct|all)\s+)?(\w+)\s*\))\s*$/i;
            const match = re.exec(stringColumn);
            if( !match )
                throw new ParseError('#QUERY_PARSER_ERROR: invalid column expression');

            const columnName = match[1] || match[4];
            const aggregate = match[2] && match[2].toLowerCase();
            const distinct = match[3] && match[3].toLowerCase() === 'distinct';
            if( aggregate && AGGREGATES.indexOf(aggregate) < 0 )
                throw new ParseError(`#QUERY_PARSER_ERROR: unknown aggregate ${aggregate}`);

            return new SelectColumn(columnName, aggregate, distinct);
        });
    }

    private _parseExprRow(rowData:Array<IValue>)
    {
        const firstWord = this._parseAsString('query token', rowData[0]);
        switch( firstWord.toLowerCase() )
        {
            case '(':
            case ')':
            case 'and':
            case 'or':
                this.conditionTokens.push({
                    type: firstWord.toLowerCase()
                });
                break;
            default:
                this.conditionTokens.push({
                    type: 'condition',
                    condition: this._parseConditionRow(rowData)
                });
                break;
        }
    }

    private _parseConditionRow(rowData:Array<IValue>):QueryCondition
    {
        const firstWord = this._parseAsString('columnName', rowData[0]);
        if( !firstWord.startsWith('@') )
            throw new ParseError('#QUERY_PARSE_ERROR: condition should start with column ref @<column>');

        const columnName = firstWord.substring(1);
        const operator = this._parseAsString('operator', rowData[1]).toLowerCase();

        switch( operator )
        {
            case '>':
            case '<':
            case '=':
            case '!=':
            case '>=':
            case '<=':
                const value = rowData[2];
                return new BinaryCondition(operator, columnName, serialize(value));
            case 'in':
            {
                const value = rowData[2];
                if( !Type.isInstanceOf(value, Type.Frame) )
                    throw new ParseError('#QUERY_PARSE_ERROR: IN expects frame as parameter');

                const values = (value as IFrame).getAllValues()
                    .filter(value => !value.isEmpty)
                    .map(value => serialize(value));

                return new InCondition(columnName, values);
            }
            case 'between':
            {
                const value = rowData[3];
                const params = Type.isInstanceOf(value, Type.Frame)
                    ? (value as IFrame).getAllValues()
                    : null;

                const min = params ? params[0] : rowData[3];
                const max = params ? params[1] : rowData[4];

                if( !min || !max )
                    throw new ParseError('#QUERY_PARSE_ERROR: invalid BETWEEN clause parameters');

                return new BetweenCondition(columnName, serialize(min), serialize(max));
            }
            case 'like':
            case 'not like':
            case 'ilike':
            case 'not ilike':
            {
                const pattern = this._parseAsString('pattern', rowData[2]);
                return new LikeCondition(columnName, pattern, operator.indexOf('not') >= 0, operator.indexOf('ilike') >= 0);
            }
        }

        throw new ParseError(`#QUERY_PARSE_ERROR: unknown condition operator ${operator}`);
    }

    private _parseAsInt(entity:string, value:IValue):number
    {
        const num = CoercionUtils.coerce(value, Type.Integer, null);
        if( !Type.isInstanceOf(num, Type.Integer) )
            throw new ParseError(`#QUERY_PARSER_ERROR: invalid ${entity} value`);
        return (num as AInteger).value;
    }

    private _parseAsString(entity:string, value:IValue):string
    {
        const s = CoercionUtils.coerce(value, Type.String, null);
        if( !Type.isInstanceOf(s, Type.String) )
            throw new ParseError(`#QUERY_PARSER_ERROR: invalid ${entity} value`);
        return (s as AString).value;
    }

    private _parseStringValues(entity:string, values:Array<IValue>):Array<string>
    {
        const result = [];
        for( let value of values )
        {
            if( value.isEmpty )
                break;

            const s = CoercionUtils.coerce(value, Type.String, null);
            if( !Type.isInstanceOf(s, Type.String) )
                throw new ParseError(`#QUERY_PARSER_ERROR: invalid ${entity} value`);

            result.push((s as AString).value);
        }
        return result;
    }
}
